// src/pages/GuardsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import GuardsHeader from '../components/guards/GuardsHeader';
import GuardsStats from '../components/guards/GuardsStats';
import GuardsFilters from '../components/guards/GuardsFilters';
import GuardsTable from '../components/guards/GuardsTable';
import GuardFormSlideOver from '../components/guards/GuardFormSlideOver';
import DeleteGuardDialog from '../components/guards/DeleteGuardDialog';

// Adjust if you use env variable
const API_BASE_URL = 'https://patrol-visit.patroll-management.nissiindia.com';

const GuardsPage = () => {
  // Filter / query state
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Data state
  const [guards, setGuards] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingGuard, setEditingGuard] = useState(null);

  const [deletingGuard, setDeletingGuard] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Derived stats
  const totalGuards = pagination.total || guards.length;
  const activeGuards = useMemo(
    () => guards.filter((g) => g.status === 'active').length,
    [guards]
  );
  const inactiveGuards = useMemo(
    () => guards.filter((g) => g.status === 'inactive').length,
    [guards]
  );
  const recentlyJoined = useMemo(
    () => guards.filter((g) => g.date_of_joining).length,
    [guards]
  );

  // Fetch list
  const fetchGuards = async (opts = {}) => {
    try {
      setLoading(true);
      setError('');
      const currentPage = opts.page || page;

      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', limit.toString());
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (search.trim()) {
        params.append('search', search.trim());
      }

      const res = await fetch(
        `${API_BASE_URL}/api/guards?${params.toString()}`
      );
      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to load guards');
      }

      setGuards(json.data || []);
      setPagination(
        json.pagination || { page: 1, limit, total: 0, totalPages: 1 }
      );
      setPage(json.pagination?.page || currentPage);
    } catch (err) {
      console.error('[fetchGuards]', err);
      setError(err.message || 'Failed to load guards');
      toast.error(err.message || 'Failed to load guards');
    } finally {
      setLoading(false);
    }
  };

  // Fetch when filter/search changes (reset to page 1)
  useEffect(() => {
    fetchGuards({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  // Fetch when page changes
  useEffect(() => {
    fetchGuards({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Create button
  const handleOpenCreate = () => {
    setEditingGuard(null);
    setShowForm(true);
  };

  // Edit button
  const handleOpenEdit = (guard) => {
    setEditingGuard(guard);
    setShowForm(true);
  };

  // Save (create / update)
  const handleSaveGuard = async (data, { editingGuard: guardToEdit }) => {
    try {
      setSaving(true);
      setError('');

      const payload = {
        first_name: data.first_name.trim(),
        last_name: data.last_name?.trim() || null,
        email: data.email.trim(),
        phone: data.phone.trim(),
        employee_code: data.employee_code?.trim() || null,
        status: data.status,
        date_of_joining: data.date_of_joining || null,
        notes: data.notes?.trim() || null,
        username: data.username.trim(),
      };

      if (guardToEdit) {
        if (data.password && data.password.trim() !== '') {
          payload.password = data.password.trim();
        }
      } else {
        payload.password = data.password.trim();
      }

      let url = `${API_BASE_URL}/api/guards`;
      let method = 'POST';

      if (guardToEdit) {
        url = `${API_BASE_URL}/api/guards/${guardToEdit.id}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to save guard');
      }

      await fetchGuards({ page: 1 });
      setShowForm(false);
      setEditingGuard(null);

      toast.success(
        guardToEdit ? 'Guard updated successfully' : 'Guard created successfully'
      );
    } catch (err) {
      console.error('[saveGuard]', err);
      setError(err.message || 'Failed to save guard');
      toast.error(err.message || 'Failed to save guard');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const confirmDelete = (guard) => {
    setDeletingGuard(guard);
  };

  const handleDelete = async () => {
    if (!deletingGuard) return;
    setDeleting(true);
    setError('');

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/guards/${deletingGuard.id}`,
        { method: 'DELETE' }
      );
      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to delete guard');
      }

      await fetchGuards({ page: 1 });
      setDeletingGuard(null);
      toast.success('Guard deleted successfully');
    } catch (err) {
      console.error('[deleteGuard]', err);
      setError(err.message || 'Failed to delete guard');
      toast.error(err.message || 'Failed to delete guard');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* Toast container */}
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'text-sm',
        }}
      />

      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        <GuardsHeader
          search={search}
          onSearchChange={setSearch}
          onAddClick={handleOpenCreate}
        />

        <GuardsStats
          totalGuards={totalGuards}
          activeGuards={activeGuards}
          inactiveGuards={inactiveGuards}
          recentlyJoined={recentlyJoined}
        />

        <GuardsFilters
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          visibleCount={guards.length}
          totalGuards={totalGuards}
        />

        <GuardsTable
          guards={guards}
          loading={loading}
          error={error}
          pagination={pagination}
          onPageChange={setPage}
          onEdit={handleOpenEdit}
          onDelete={confirmDelete}
        />

        <GuardFormSlideOver
          isOpen={showForm}
          editingGuard={editingGuard}
          totalGuards={totalGuards}
          saving={saving}
          onClose={() => {
            if (!saving) {
              setShowForm(false);
              setEditingGuard(null);
            }
          }}
          onSubmit={handleSaveGuard}
        />

        <DeleteGuardDialog
          guard={deletingGuard}
          deleting={deleting}
          onCancel={() => !deleting && setDeletingGuard(null)}
          onConfirm={handleDelete}
        />
      </main>
    </>
  );
};

export default GuardsPage;
